//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled, { useTheme } from 'styled-components';

import { LabeledCheckbox } from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { BodyErrorText, BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { Permissions } from '@models/Permissions';
import { UIATrackingIds } from '@uia/constants/uiaConstants';

interface Props {
  canShareModerationMedia: boolean;
  isError: boolean;
  isForceReviewed: boolean;
  isOpen: boolean;
  isPending: boolean;
  onCancel: () => void;
  onForceReviewedChange: (checked: boolean) => void;
  onShare: () => void;
}

export const ShareWithoutProductTagsModal = ({
  canShareModerationMedia,
  isError,
  isForceReviewed,
  isOpen,
  isPending,
  onCancel,
  onForceReviewedChange,
  onShare,
}: Props) => {
  const theme = useTheme();

  return (
    <Modal
      closeModal={isPending ? undefined : onCancel}
      contentOnKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Escape') {
          return;
        }

        event.stopPropagation();

        if (!isPending) {
          onCancel();
        }
      }}
      contents={
        <Content>
          <BodyText>
            This media isn't tagged with a product. It will be added to All
            Shared Media with the status Needs product, and won't be sent to the
            brand for review.
          </BodyText>

          <BodyText>To send it to the brand, tag a product first.</BodyText>

          <LabeledCheckbox
            handleClick={onForceReviewedChange}
            isChecked={isForceReviewed}
            isDisabled={isPending || !canShareModerationMedia}
            label='Request review now, without product tags'
            trackingId={UIATrackingIds.SHARE_FORCE_REVIEW}
          />

          {isError && (
            <BodyErrorText role='alert'>
              Failed to share this item. Please try again.
            </BodyErrorText>
          )}
        </Content>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: isPending ? 'Sharing…' : 'Share',
        isDisabled: isPending || !canShareModerationMedia,
        permissionName: Permissions.ModerationView,
        onClick: onShare,
        trackingId: UIATrackingIds.SHARE_CONFIRM,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        isDisabled: isPending,
        onClick: onCancel,
        trackingId: UIATrackingIds.SHARE_CANCEL,
      }}
      shouldHideOverlay={false}
      title='Share without product tags?'
      trackingId={UIATrackingIds.SHARE_WITHOUT_PRODUCTS_MODAL}
      width='wide'
      zIndexOverlayOverride={theme.zindex.modal}
    />
  );
};

const Content = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);
